// User Registration Types and Interfaces

/**
 * Password strength levels
 */
export enum PasswordStrength {
  WEAK = 'weak',
  FAIR = 'fair',
  GOOD = 'good',
  STRONG = 'strong',
  VERY_STRONG = 'very_strong'
}

/**
 * Password requirements configuration
 */
export interface PasswordRequirements {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  specialChars: string;
}

/**
 * Default password requirements
 */
export const DEFAULT_PASSWORD_REQUIREMENTS: PasswordRequirements = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?'
};

/**
 * User registration form data
 */
export interface RegistrationFormData {
  // Required fields
  username: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  
  // User profile
  firstName: string;
  lastName: string;
  displayName?: string;
  
  // Optional contact
  phone?: string;
  
  // Organization
  department?: string;
  organization?: string;
  
  // Agreements
  acceptTerms: boolean;
  acceptPrivacy: boolean;
  
  // Optional role (for admin-initiated registration)
  role?: string;
}

/**
 * Field validation error
 */
export interface ValidationError {
  field: string;
  message: string;
  type?: 'required' | 'format' | 'length' | 'match' | 'unique' | 'strength';
}

/**
 * Registration validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * User registration request (API payload)
 */
export interface RegistrationRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  phone?: string;
  department?: string;
  organization?: string;
  role?: string;
  captchaToken?: string;
}

/**
 * Registered user data (without sensitive info)
 */
export interface RegisteredUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  phone?: string;
  department?: string;
  organization?: string;
  role: string;
  accountStatus: 'pending' | 'active' | 'suspended';
  emailVerified: boolean;
  createdAt: Date;
  createdBy?: string;
}

/**
 * Registration API response
 */
export interface RegistrationResponse {
  success: boolean;
  user?: RegisteredUser;
  message?: string;
  error?: string;
  requiresEmailVerification?: boolean;
  verificationEmailSent?: boolean;
}

/**
 * Email verification request
 */
export interface EmailVerificationRequest {
  token: string;
  email?: string;
}

/**
 * Email verification response
 */
export interface EmailVerificationResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Username/Email availability check request
 */
export interface AvailabilityCheckRequest {
  username?: string;
  email?: string;
}

/**
 * Username/Email availability check response
 */
export interface AvailabilityCheckResponse {
  available: boolean;
  message?: string;
}

/**
 * Password strength analysis result
 */
export interface PasswordStrengthResult {
  strength: PasswordStrength;
  score: number; // 0-100
  feedback: string[];
  meetsRequirements: boolean;
  requirementsMet: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumbers: boolean;
    hasSpecialChars: boolean;
  };
}

/**
 * Account recovery request
 */
export interface PasswordResetRequest {
  email: string;
  captchaToken?: string;
}

/**
 * Account recovery response
 */
export interface PasswordResetResponse {
  success: boolean;
  message?: string;
  error?: string;
  resetEmailSent?: boolean;
}

/**
 * Password reset confirmation request
 */
export interface PasswordResetConfirmRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Password reset confirmation response
 */
export interface PasswordResetConfirmResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * User role definition
 */
export interface UserRole {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  isDefault?: boolean;
}

/**
 * Default user roles
 */
export const DEFAULT_USER_ROLES: UserRole[] = [
  {
    id: 'user',
    name: 'user',
    displayName: 'User',
    description: 'Standard user with basic access',
    isDefault: true
  },
  {
    id: 'admin',
    name: 'admin',
    displayName: 'Administrator',
    description: 'Full system access and user management'
  },
  {
    id: 'moderator',
    name: 'moderator',
    displayName: 'Moderator',
    description: 'Content moderation and user support'
  }
];
